import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Sparkles, Flame, Sunrise, Compass, AlertTriangle, Target } from "lucide-react";
import TourBackdrop from "@/components/onboarding/TourBackdrop";
import TourTooltip from "@/components/onboarding/TourTooltip";

/**
 * OnboardingTour — lightweight spotlight tour.
 *
 * Steps target elements by `data-testid`. A dark backdrop with a cutout ring
 * highlights the current target; a tooltip card is anchored near it.
 * Persists completion in localStorage (`pfo_tour_completed`).
 *
 * Usage:
 *   <OnboardingTour steps={STEPS} storageKey="pfo_tour_completed" onDone={...} />
 *
 * Step shape:
 *   { selector: string|null, title: string, body: string, icon: string|Component }
 *   Icons can be a string key (e.g. "compass", "sunrise", "flame", "sparkles",
 *   "alert", "target") or a React component. String keys make it easy for
 *   the backend to ship serialisable tour plans.
 */

const ICON_MAP = {
  compass: Compass,
  sunrise: Sunrise,
  flame: Flame,
  sparkles: Sparkles,
  alert: AlertTriangle,
  target: Target,
};

const PAD = 10;              // cutout padding around target
const TIP_W = 340;           // tooltip width
const TIP_GAP = 16;          // gap between cutout and tooltip

function useTargetRect(selector) {
  const [rect, setRect] = useState(null);
  useLayoutEffect(() => {
    if (!selector) { setRect(null); return; }
    let el = null;
    let raf = 0;
    const measure = () => {
      el = document.querySelector(selector);
      if (el) {
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      } else {
        setRect(null);
      }
    };
    // Retry a few frames — targets like the nudge card mount async
    let tries = 0;
    const tryMeasure = () => {
      measure();
      tries += 1;
      if ((!document.querySelector(selector)) && tries < 40) {
        raf = requestAnimationFrame(tryMeasure);
      }
    };
    tryMeasure();
    const onResize = () => measure();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
  }, [selector]);
  return rect;
}

export default function OnboardingTour({ steps, storageKey = "pfo_tour_completed", onDone }) {
  const { t } = useTranslation();
  const [active, setActive] = useState(false);
  const [idx, setIdx] = useState(0);
  const startedRef = useRef(false);

  // If any step has a translation key (`titleKey` / `bodyKey`), resolve
  // them through the active locale. Falls back to the literal
  // `title` / `body` fields for legacy tours or backend-shipped copy.
  const resolvedSteps = useMemo(() => {
    if (!steps) return [];
    return steps.map((s) => ({
      ...s,
      title: s.titleKey ? t(s.titleKey, s.params || {}) : s.title,
      body: s.bodyKey ? t(s.bodyKey, s.params || {}) : s.body,
    }));
  }, [steps, t]);

  // Start once — only if not previously completed
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    // NOTE: pfo_tour_completed stores only an ISO timestamp string marking
    // that the user has seen the tour. It is NOT a credential or session
    // token — no XSS risk. localStorage is the correct place for UX flags
    // that must persist per-device and per-user without server round-trips.
    try {
      const done = localStorage.getItem(storageKey);
      if (!done) setActive(true);
    } catch (e) { console.warn("Tour: localStorage read failed", e); }
  }, [storageKey]);

  const step = resolvedSteps.length > 0 ? resolvedSteps[idx] : null;
  const rect = useTargetRect(active && step ? step.selector : null);

  // If step has a selector but element isn't found after ~1.2s, skip to next
  useEffect(() => {
    if (!active || !step?.selector) return;
    const timer = setTimeout(() => {
      if (!document.querySelector(step.selector)) {
        setIdx(i => Math.min(i + 1, resolvedSteps.length - 1));
      }
    }, 1200);
    return () => clearTimeout(timer);
  }, [active, idx, step, resolvedSteps]);

  // Ensure target is scrolled into view
  useEffect(() => {
    if (!active || !step?.selector) return;
    const el = document.querySelector(step.selector);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [active, idx, step]);

  if (!active || !step) return null;

  const finish = (completed = true) => {
    setActive(false);
    if (completed) {
      // Non-credential UX flag: just an ISO timestamp marking completion so
      // we don't nag the user on every subsequent visit.
      try { localStorage.setItem(storageKey, new Date().toISOString()); }
      catch (e) { console.warn("Tour: localStorage write failed", e); }
    }
    if (onDone) onDone(completed);
  };

  const next = () => {
    if (idx >= resolvedSteps.length - 1) finish(true);
    else setIdx(idx + 1);
  };
  const skip = () => finish(true); // treat skip as "seen"

  // Compute cutout + tooltip positions
  const hasTarget = !!rect;
  const cutout = hasTarget
    ? { top: rect.top - PAD, left: rect.left - PAD, width: rect.width + PAD * 2, height: rect.height + PAD * 2 }
    : null;

  // Tooltip placement: below the target if room, else above; else center
  let tipStyle;
  if (cutout) {
    const vw = window.innerWidth, vh = window.innerHeight;
    const belowY = cutout.top + cutout.height + TIP_GAP;
    const aboveY = cutout.top - TIP_GAP - 180; // approx tooltip height reserve
    const preferBelow = belowY + 180 < vh;
    const top = preferBelow ? belowY : Math.max(16, aboveY);
    // horizontal: center on target, but clamp to viewport
    let left = cutout.left + cutout.width / 2 - TIP_W / 2;
    left = Math.max(16, Math.min(left, vw - TIP_W - 16));
    tipStyle = { top, left, width: TIP_W };
  } else {
    // Center tooltip when no target (welcome/finale steps)
    tipStyle = {
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: TIP_W,
    };
  }

  const IconEl = (typeof step.icon === "string" ? ICON_MAP[step.icon] : step.icon) || Compass;

  return createPortal(
    <div className="fixed inset-0 z-[9999]" data-testid="onboarding-tour" aria-modal="true" role="dialog">
      <TourBackdrop cutout={cutout} />
      <TourTooltip
        t={t}
        style={tipStyle}
        step={step}
        IconEl={IconEl}
        idx={idx}
        totalSteps={resolvedSteps.length}
        onNext={next}
        onSkip={skip}
      />
    </div>,
    document.body,
  );
}

// Default step set — used as a fallback when the personalised backend plan
// is unavailable. `titleKey` / `bodyKey` are resolved through i18n at
// render time. Uses string icon keys (compass/sunrise/flame/sparkles)
// so shape matches what the backend returns.
export const DASHBOARD_TOUR_STEPS = [
  {
    selector: null,
    titleKey: "tour.welcome_title",
    bodyKey: "tour.welcome_body",
    icon: "compass",
  },
  {
    selector: "[data-testid=\"dashboard-nudge-card\"]",
    titleKey: "tour.nudge_title",
    bodyKey: "tour.nudge_body",
    icon: "sunrise",
  },
  {
    selector: "[data-testid=\"dashboard-streak-banner\"]",
    titleKey: "tour.streak_title",
    bodyKey: "tour.streak_body",
    icon: "flame",
  },
  {
    selector: "[data-testid=\"dashboard-ask-ai-btn\"]",
    titleKey: "tour.advisor_title",
    bodyKey: "tour.advisor_body",
    icon: "sparkles",
  },
];
